import { Link } from "react-router-dom";
import LogoBadge from "@/components/LogoBadge";
import Footer from "@/components/Footer";

const SectionHeading = ({ children }) => (
  <h2
    className="font-serif text-2xl sm:text-3xl text-[#161618] mt-14 mb-5 tracking-tight"
    style={{ fontFamily: "'Cormorant Garamond', serif" }}
  >
    {children}
  </h2>
);

const Paragraph = ({ children }) => (
  <p className="text-[#3a3a3c] font-light leading-relaxed text-base sm:text-[1.05rem] mb-5">
    {children}
  </p>
);

const Bullets = ({ items }) => (
  <ul className="space-y-2 mb-6 pl-1">
    {items.map((item, i) => (
      <li
        key={i}
        className="flex items-start gap-3 text-[#3a3a3c] font-light leading-relaxed"
      >
        <span className="mt-2 inline-block w-1 h-1 rounded-full bg-[#B6AA96] flex-shrink-0" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

export default function Terms() {
  return (
    <div
      data-testid="terms-page"
      className="bg-[#F1EDDE] text-[#161618] min-h-screen"
    >
      {/* Minimal header */}
      <header className="border-b border-[#B6AA96]/30">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 py-6 flex items-center justify-between">
          <Link
            to="/"
            data-testid="terms-home-link"
            className="inline-flex items-center"
          >
            <LogoBadge variant="warmblack" size="sm" />
          </Link>
          <Link
            to="/"
            data-testid="terms-back-link"
            className="text-[0.65rem] uppercase tracking-[0.3em] text-[#161618] hover:text-[#B6AA96] transition-colors"
          >
            Back to site
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-10 py-16 sm:py-24">
        <div className="text-[0.65rem] uppercase tracking-[0.4em] text-[#B6AA96] mb-6">
          Legal
        </div>
        <h1
          className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#161618] tracking-tight leading-[1.05]"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Booking Terms & Conditions
        </h1>

        <div className="mt-10">
          <Paragraph>
            Thank you for choosing Stacy Fox Laser and Advanced Aesthetics.
            These terms are in place to ensure appointments run smoothly and
            safely for all clients.
          </Paragraph>

          <SectionHeading>Booking Fees</SectionHeading>
          <Paragraph>
            A booking fee is required to secure all appointments and will be
            deducted from the cost of your treatment.
          </Paragraph>
          <Paragraph>
            Booking fees are non-refundable and non-transferable if:
          </Paragraph>
          <Bullets
            items={[
              "You fail to attend your appointment.",
              "You cancel with less than 48 hours' notice.",
              "You request to move your appointment within 48 hours of the scheduled time.",
            ]}
          />
          <Paragraph>
            This allows clinic time to be managed fairly and gives other
            clients the opportunity to book available appointments.
          </Paragraph>

          <SectionHeading>Arriving for Your Appointment</SectionHeading>
          <Paragraph>Please arrive on time.</Paragraph>
          <Paragraph>
            If you are more than 5 minutes late, I may not have enough time to
            safely complete your treatment. In these cases, your appointment
            may need to be rearranged and your booking fee may be lost.
          </Paragraph>
          <Paragraph>
            If you are running late, please contact me as soon as possible.
          </Paragraph>

          <SectionHeading>Before Your Appointment</SectionHeading>
          <Paragraph>
            Where possible, please attend your appointment without makeup in
            the treatment area.
          </Paragraph>
          <Paragraph>
            For 24–48 hours before treatment, avoid alcohol, excessive
            exercise, and any other activities advised against in your
            treatment information or consent forms.
          </Paragraph>

          <SectionHeading>Health & Safety</SectionHeading>
          <Paragraph>Your safety will always come first.</Paragraph>
          <Paragraph>
            Appointments may need to be postponed if you are unwell, taking
            antibiotics, have an active cold sore, skin infection, or any
            other condition that could affect treatment safety or results.
          </Paragraph>

          <SectionHeading>Consultation & Treatment Suitability</SectionHeading>
          <Paragraph>
            All treatments require a consultation and assessment before
            proceeding.
          </Paragraph>
          <Paragraph>
            I reserve the right to refuse, postpone, or adapt treatment if I
            do not feel it is suitable, safe, or in your best interests.
          </Paragraph>

          <SectionHeading>Children in Clinic</SectionHeading>
          <Paragraph>
            Unfortunately, children cannot attend appointments.
          </Paragraph>
          <Paragraph>
            This is due to safety, insurance, and the nature of the treatments
            carried out within the clinic. Please arrange childcare before
            your appointment.
          </Paragraph>

          <SectionHeading>Medical Information</SectionHeading>
          <Paragraph>
            Before treatment, you will be asked to complete a consultation and
            medical history form.
          </Paragraph>
          <Paragraph>
            It is important that all information provided is accurate and up
            to date. This includes medical conditions, medications, allergies,
            previous treatments, and any changes to your health.
          </Paragraph>
          <Paragraph>
            Failure to provide relevant medical information could affect your
            treatment and may increase the risk of complications.
          </Paragraph>
          <Paragraph>
            Please let me know if anything changes between appointments.
          </Paragraph>

          <SectionHeading>Clinical Photography</SectionHeading>
          <Paragraph>
            Photographs may be taken before and after treatment for clinical
            records, treatment planning, and progress tracking.
          </Paragraph>
          <Paragraph>
            You may also be asked for permission to use photographs for
            educational or marketing purposes. If you would prefer your images
            not to be used publicly, please let me know.
          </Paragraph>

          <SectionHeading>Results</SectionHeading>
          <Paragraph>
            Results vary from person to person and cannot be guaranteed.
          </Paragraph>
          <Paragraph>
            The way your body responds to treatment, your anatomy, lifestyle,
            age, and aftercare can all affect your final result and how long
            it lasts.
          </Paragraph>

          <SectionHeading>Age Restrictions</SectionHeading>
          <Paragraph>
            All treatments are strictly for clients aged 18 years and over.
          </Paragraph>
          <Paragraph>
            Identification may be requested where necessary.
          </Paragraph>

          <SectionHeading>Anti-Wrinkle Reviews</SectionHeading>
          <Paragraph>
            A review appointment is recommended around two weeks after
            anti-wrinkle treatment.
          </Paragraph>
          <Paragraph>
            This allows the treatment to be assessed once it has fully settled
            and, where appropriate, any small adjustments can be discussed.
          </Paragraph>
        </div>
      </main>

      <Footer />
    </div>
  );
}
